// src/AdminStudents.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "./firebase";
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

export default function AdminStudents() {
  const navigate = useNavigate();

  const BUS_COUNT = 15;
  const buses = useMemo(
    () => Array.from({ length: BUS_COUNT }, (_, i) => `bus${i + 1}`),
    []
  );

  const [counts, setCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsErr, setCountsErr] = useState("");

  const [selectedBus, setSelectedBus] = useState(null);

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsErr, setStudentsErr] = useState("");
  const [sq, setSq] = useState("");

  const [loggingOut, setLoggingOut] = useState(false);

  const studentsUnsubRef = useRef(null);

  const refreshCounts = async () => {
    setCountsErr("");
    setCountsLoading(true);

    try {
      const m = {};
      for (const bid of buses) {
        // NOTE: simple count (reads all docs). ok for demo.
        const snap = await getDocs(collection(db, "buses", bid, "students"));
        m[bid] = snap.size || 0;
      }
      setCounts(m);
    } catch (e) {
      setCountsErr(e?.message || "Failed to refresh counts.");
    } finally {
      setCountsLoading(false);
    }
  };

  useEffect(() => {
    refreshCounts();
    // eslint-disable-next-line
  }, []);

  // subscribe selected bus students
  useEffect(() => {
    if (studentsUnsubRef.current) {
      try {
        studentsUnsubRef.current();
      } catch (e) {}
      studentsUnsubRef.current = null;
    }

    setStudents([]);
    setStudentsErr("");

    if (!selectedBus) return;

    setLoadingStudents(true);

    const qRef = query(
      collection(db, "buses", selectedBus, "students"),
      orderBy("updatedAt", "desc")
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setStudents(arr);
        setLoadingStudents(false);
      },
      (e) => {
        setStudentsErr(e?.message || "Permission denied / failed to load students.");
        setLoadingStudents(false);
      }
    );

    studentsUnsubRef.current = unsub;

    return () => {
      if (studentsUnsubRef.current) {
        try {
          studentsUnsubRef.current();
        } catch (e) {}
        studentsUnsubRef.current = null;
      }
    };
  }, [selectedBus]);

  const filteredStudents = useMemo(() => {
    const t = sq.trim().toLowerCase();
    if (!t) return students;
    return students.filter((s) => {
      const year = String(s.year ?? "").toLowerCase();
      const stop = String(s.stopName ?? "").toLowerCase();
      const email = String(s.updatedByEmail ?? "").toLowerCase();
      return year.includes(t) || stop.includes(t) || email.includes(t);
    });
  }, [students, sq]);

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await signOut(auth);
    } catch (e) {}
    navigate("/admin-login", { replace: true });
    setLoggingOut(false);
  };

  return (
    <div className="admin-students">
      <style>{`
        .admin-students{
          min-height:100vh;
          padding: 26px 0 46px;
          background:
            radial-gradient(900px 600px at 15% 15%, rgba(47,123,255,.26), transparent 60%),
            radial-gradient(900px 600px at 85% 25%, rgba(255,77,109,.14), transparent 60%),
            radial-gradient(1000px 700px at 50% 95%, rgba(32,201,151,.12), transparent 60%),
            linear-gradient(180deg, #060a13 0%, #0b1220 100%);
        }

        .glass-card{
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 22px;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 18px 50px rgba(0,0,0,.42);
          color: #fff;
        }

        .subtxt{ color: rgba(234,240,255,.72); font-weight: 800; font-size: 13px; }

        .btnx{
          border-radius: 14px;
          padding: 10px 14px;
          font-weight: 900;
          border: 0;
        }
        .btn-outlinex{
          border-radius: 14px;
          padding: 10px 14px;
          font-weight: 900;
          border: 1px solid rgba(255,255,255,.22);
          background: rgba(255,255,255,.06);
          color: rgba(255,255,255,.92);
        }
        .btn-outlinex:hover{ background: rgba(255,255,255,.12); }

        /* ✅ full-color top buttons */
        .btn-studentlist{
          color:#071b10;
          background: linear-gradient(135deg, rgba(21,199,119,1), rgba(34,197,94,1));
          box-shadow: 0 14px 34px rgba(21,199,119,.22);
        }
        .btn-route{
          color:#fff;
          background: linear-gradient(135deg, rgba(47,123,255,1), rgba(106,166,255,1));
          box-shadow: 0 14px 34px rgba(47,123,255,.22);
        }
        .btn-logout{
          color:#fff;
          background: linear-gradient(135deg, rgba(255,77,109,1), rgba(220,53,69,1));
          box-shadow: 0 14px 34px rgba(255,77,109,.18);
        }
        .btn-refresh{
          color:#0b0214;
          background: linear-gradient(135deg, rgba(168,85,247,1), rgba(236,72,153,1));
          box-shadow: 0 14px 34px rgba(168,85,247,.20);
        }

        .hr-soft{ border-color: rgba(255,255,255,.12); }

        .bus-grid{
          display:grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        @media (max-width: 992px){
          .bus-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 576px){
          .bus-grid{ grid-template-columns: 1fr; }
        }

        .bus-card{
          border-radius: 18px;
          padding: 16px;
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.06);
          cursor: pointer;
          transition: transform .15s ease, background .15s ease;
        }
        .bus-card:hover{
          transform: translateY(-2px);
          background: rgba(255,255,255,.08);
        }
        .bus-card--active{
          border-color: rgba(21,199,119,.40);
          box-shadow: 0 0 0 1px rgba(21,199,119,.12), 0 18px 52px rgba(21,199,119,.12);
        }

        .bus-name{ font-weight: 900; font-size: 18px; }
        .muted{ color: rgba(234,240,255,.70); font-size: 12px; font-weight: 800; }

        .glass-input{
          background: rgba(255,255,255,.10) !important;
          border: 1px solid rgba(255,255,255,.16) !important;
          color: rgba(255,255,255,.92) !important;
          border-radius: 14px !important;
          padding: 12px 12px !important;
        }
        .glass-input::placeholder{ color: rgba(234,240,255,.45); }

        .table-wrap{ border-radius: 18px; overflow: hidden; border: 1px solid rgba(255,255,255,.14); }
        table{ color: rgba(255,255,255,.92) !important; margin: 0 !important; }
        thead th{
          position: sticky; top: 0; z-index: 2;
          background: rgba(10,18,48,.80) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-color: rgba(255,255,255,.10) !important;
        }
        td, th{ border-color: rgba(255,255,255,.10) !important; vertical-align: middle !important; }
        tr:hover{ background: rgba(255,255,255,.04); }
      `}</style>

      <div className="container">
        <div className="glass-card p-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h2 className="fw-bold m-0">Admin • Student List</h2>
              <div className="subtxt mt-1">
                Bus-wise students (Year + Stop). Students cannot view lists.
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              {/* ✅ REPLACED "Admin Panel" with "Student List" (same page) */}
              <button className="btnx btn-studentlist" onClick={() => navigate("/admin", { replace: true })}>
                Student List
              </button>

              <button className="btnx btn-route" onClick={() => navigate("/admin/route-editor")}>
                Route Editor
              </button>

              <button className="btnx btn-logout" onClick={logout} disabled={loggingOut}>
                Logout
              </button>
            </div>
          </div>

          <hr className="hr-soft" />

          {countsErr ? <div className="alert alert-warning">{countsErr}</div> : null}

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="subtxt">
              {selectedBus ? `Selected: ${selectedBus.toUpperCase()}` : "Select a bus to view students."}
            </div>
            <button className="btnx btn-refresh" onClick={refreshCounts} disabled={countsLoading}>
              {countsLoading ? "Refreshing..." : "Refresh Counts"}
            </button>
          </div>

          <div className="bus-grid">
            {buses.map((bid) => {
              const n = counts[bid] ?? 0;
              const active = selectedBus === bid;
              return (
                <div
                  key={bid}
                  className={`bus-card ${active ? "bus-card--active" : ""}`}
                  onClick={() => {
                    setSelectedBus(bid);
                    setSq("");
                  }}
                >
                  <div className="bus-name">{bid.toUpperCase()}</div>
                  <div className="muted mt-2">Total Students: {n}</div>
                  <div className="muted mt-1">Click to open list</div>
                </div>
              );
            })}
          </div>

          {/* Students table */}
          {selectedBus ? (
            <>
              <hr className="hr-soft" />

              <div className="row g-2 align-items-center">
                <div className="col-12 col-lg-6">
                  <input
                    className="form-control glass-input"
                    placeholder="Search (year / stop / email)..."
                    value={sq}
                    onChange={(e) => setSq(e.target.value)}
                  />
                </div>
                <div className="col-12 col-lg-6 d-flex justify-content-lg-end">
                  <div className="pill" style={{ width: "fit-content" }}>
                    <div className="muted">Showing</div>
                    <div className="fw-bold">{filteredStudents.length}</div>
                  </div>
                </div>
              </div>

              {studentsErr ? <div className="alert alert-danger mt-3 mb-0">{studentsErr}</div> : null}

              <div className="table-wrap mt-3">
                <div className="table-responsive" style={{ maxHeight: "70vh" }}>
                  <table className="table table-bordered align-middle text-center">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>#</th>
                        <th style={{ width: 140 }}>Year</th>
                        <th>Stop</th>
                        <th style={{ width: 230 }}>Updated By</th>
                        <th style={{ width: 210 }}>Updated At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingStudents ? (
                        Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                          </tr>
                        ))
                      ) : filteredStudents.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-4" style={{ color: "rgba(255,255,255,.75)" }}>
                            No student details yet.
                          </td>
                        </tr>
                      ) : (
                        filteredStudents.map((s, idx) => (
                          <tr key={s.id}>
                            <td className="fw-bold">{idx + 1}</td>
                            <td>{s.year ?? "—"}</td>
                            <td style={{ textAlign: "left" }}>{s.stopName ?? "—"}</td>
                            <td style={{ fontSize: 13 }}>{s.updatedByEmail || "—"}</td>
                            <td style={{ fontSize: 13 }}>
                              {s.updatedAt?.toDate ? s.updatedAt.toDate().toLocaleString() : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}